import type { JSX } from "react";
import { useState } from "react";
import { subDays, startOfDay, endOfDay } from "date-fns";
import { Filter, SlidersHorizontal } from "lucide-react";
import type { SegmentDefinition } from "@adlywise/shared";
import {
  ConfigureDrawer,
  createDefaultAnalyticsConfig,
  type AnalyticsConfig,
} from "@/features/analytics/components/ConfigurePanel";
import { AnalyticsAggregateFiltersDrawer } from "@/features/analytics/components/AnalyticsAggregateFiltersDrawer";
import { AggregatedResultsTable } from "@/features/analytics/components/AggregatedResultsTable";
import { useAnalyticsAggregate } from "@/features/analytics/hooks/useAnalyticsAggregate";
import DateRangePicker from "@/components/common/DateRangePicker";
import { usePageTitle } from "@/components/common/AppShell";

type PickerRange = { from: Date; to: Date };

const defaultRange = (): PickerRange => {
  const to = endOfDay(new Date());
  const from = startOfDay(subDays(new Date(), 6));
  return { from, to };
};

const EMPTY_FILTER: SegmentDefinition = { all: [] };

const cloneFilter = (value: SegmentDefinition): SegmentDefinition => ({
  all: [...(value.all ?? [])],
});

const AnalyticsPage = (): JSX.Element => {
  usePageTitle("Analytics");
  const [config, setConfig] = useState<AnalyticsConfig>(
    createDefaultAnalyticsConfig,
  );
  const [pickerRange, setPickerRange] = useState<PickerRange>(defaultRange);
  const [isConfigureDrawerOpen, setIsConfigureDrawerOpen] = useState(false);
  const [isFiltersDrawerOpen, setIsFiltersDrawerOpen] = useState(false);
  const [appliedFilter, setAppliedFilter] =
    useState<SegmentDefinition>(EMPTY_FILTER);
  const [draftFilter, setDraftFilter] = useState<SegmentDefinition>(EMPTY_FILTER);

  const {
    rows,
    truncated,
    hasRun,
    isLoading,
    isRunReportButtonDisabled,
    isRequestCancelable,
    runReportButtonLabel,
    errorMessage,
    handleRunReportButtonClick,
  } = useAnalyticsAggregate({
    config,
    range: pickerRange,
    filter: appliedFilter,
  });

  const activeFiltersCount = (appliedFilter.all ?? []).length;

  const handleOpenFilters = (): void => {
    setDraftFilter(cloneFilter(appliedFilter));
    setIsFiltersDrawerOpen(true);
  };

  const handleCloseFilters = (): void => {
    setIsFiltersDrawerOpen(false);
  };

  const handleApplyFilters = (): void => {
    setAppliedFilter(cloneFilter(draftFilter));
    setIsFiltersDrawerOpen(false);
  };

  const handleResetFilters = (): void => {
    setDraftFilter(cloneFilter(EMPTY_FILTER));
  };

  return (
    <section className="flex min-w-0 flex-col gap-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <p className="text-[13.5px] text-(--text-muted)">
          Group ad requests or responses by any set of properties over time.
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <DateRangePicker value={pickerRange} onChange={setPickerRange} />
          <button
            type="button"
            onClick={() => setIsConfigureDrawerOpen(true)}
            className="inline-flex items-center gap-2 rounded-(--radius-md) border border-(--border-strong) bg-(--surface-1) px-[14px] py-[9px] text-[13px] font-semibold text-(--text-primary) transition-colors hover:border-(--brand-primary)"
          >
            <SlidersHorizontal
              size={15}
              strokeWidth={1.8}
              className="text-(--brand-primary)"
            />
            Configure
          </button>
          <button
            type="button"
            onClick={handleOpenFilters}
            className="inline-flex items-center gap-2 rounded-(--radius-md) border border-(--border-strong) bg-(--surface-1) px-[14px] py-[9px] text-[13px] font-semibold text-(--text-primary) transition-colors hover:border-(--brand-primary)"
          >
            <Filter
              size={15}
              strokeWidth={1.8}
              className="text-(--brand-primary)"
            />
            Filters
            {activeFiltersCount > 0 && (
              <span className="inline-flex min-w-[22px] items-center justify-center rounded-(--radius-pill) bg-(--pastel-violet) px-2 py-0.5 text-[11px] font-bold text-(--brand-primary)">
                {activeFiltersCount}
              </span>
            )}
          </button>
          <button
            type="button"
            onClick={handleRunReportButtonClick}
            disabled={isRunReportButtonDisabled}
            className={`inline-flex items-center gap-2 rounded-(--radius-md) px-[18px] py-[10px] text-[13px] font-bold transition-colors ${
              isRequestCancelable
                ? "bg-(--status-danger) text-white hover:opacity-90"
                : isRunReportButtonDisabled
                  ? "cursor-not-allowed bg-(--brand-primary)/60 text-(--brand-primary-contrast)"
                  : "bg-(--brand-primary) text-(--brand-primary-contrast) hover:bg-(--brand-primary-hover)"
            }`}
          >
            {runReportButtonLabel}
          </button>
        </div>
      </div>

      <AggregatedResultsTable
        rows={rows}
        config={config}
        truncated={truncated}
        hasRun={hasRun}
        isLoading={isLoading}
        errorMessage={errorMessage}
      />

      <ConfigureDrawer
        isOpen={isConfigureDrawerOpen}
        config={config}
        onChange={setConfig}
        onClose={() => setIsConfigureDrawerOpen(false)}
      />

      <AnalyticsAggregateFiltersDrawer
        isOpen={isFiltersDrawerOpen}
        config={config}
        value={draftFilter}
        onChange={setDraftFilter}
        onClose={handleCloseFilters}
        onApply={handleApplyFilters}
        onReset={handleResetFilters}
      />
    </section>
  );
};

export default AnalyticsPage;
